import { motion } from 'framer-motion'
import { Progress } from '@/components/ui'
import type { BreathingExercise } from '../exercises'
import { circleScale, type PhasePosition } from '../timing'

export type BreathingCircleProps = {
  exercise: BreathingExercise
  position: PhasePosition
  paused: boolean
  reducedMotion: boolean
}

/**
 * Animated breathing circle synced to the phase pattern (framer-motion).
 * Reduced-motion variant: static circle with text cues and a progress bar
 * only — no scaling animation.
 */
export function BreathingCircle({ exercise, position, paused, reducedMotion }: BreathingCircleProps) {
  const scale = circleScale(position)

  return (
    <div className="breathing-circle" data-phase={position.phase.kind}>
      <div className="breathing-circle__stage" aria-hidden="true">
        {reducedMotion ? (
          <div className="breathing-circle__orb breathing-circle__orb--static" />
        ) : (
          <motion.div
            className="breathing-circle__orb"
            initial={false}
            animate={{ scale }}
            transition={{ duration: 0.15, ease: 'linear' }}
          />
        )}
      </div>

      <p className="breathing-circle__phase-label">
        {paused ? 'Gepauzeerd' : position.phase.label}
      </p>

      {exercise.alternateNostril && position.phase.side && (
        <div className="breathing-nostril" role="group" aria-label="Neusgat-aanduiding">
          <span
            className="breathing-nostril__side"
            data-active={position.phase.side === 'links' || undefined}
            aria-current={position.phase.side === 'links' ? 'true' : undefined}
          >
            Links
            {position.phase.side === 'links' && <span className="breathing-nostril__marker">actief</span>}
          </span>
          <span
            className="breathing-nostril__side"
            data-active={position.phase.side === 'rechts' || undefined}
            aria-current={position.phase.side === 'rechts' ? 'true' : undefined}
          >
            Rechts
            {position.phase.side === 'rechts' && <span className="breathing-nostril__marker">actief</span>}
          </span>
        </div>
      )}

      <div className="breathing-circle__progress">
        <Progress value={position.phaseProgress} aria-label="Voortgang van deze ademfase" />
      </div>
    </div>
  )
}
